/**
 * Service API typé pour les appels au backend
 * Utilise axios avec timeout et gestion d'erreurs
 */

import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  AxiosError,
} from "axios";
import { ToastOptions, ToastHandler } from "../types/toastOptions.d";
import { TranslationService } from "./TranslationService";
import { WidgetLanguage } from "../types/widgets.d";
import { ApiServiceConfig } from "../types/service.d";

export class ApiService {
  private axiosInstance: AxiosInstance;
  private toastHandler?: ToastHandler;
  private defaultToastOptions: ToastOptions;
  private language: WidgetLanguage;

  constructor(config: ApiServiceConfig) {
    this.toastHandler = config.toastHandler;
    this.language = config.language || WidgetLanguage.FR;
    this.defaultToastOptions = {
      toastOnSuccess: false,
      toastOnError: true,
      successMessage: null,
      errorMessage: null,
      duration: undefined,
      ...config.defaultToastOptions,
    };

    this.axiosInstance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 10000,
      headers: {
        "Content-Type": "application/json",
        ...config.headers,
      },
    });

    // Intercepteur pour gérer FormData
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Si les données sont un FormData ou une méthode GET, ne pas définir Content-Type
        // Axios le définira automatiquement avec la boundary appropriée
        if (config.data instanceof FormData || config.method === "GET") {
          // Pour FormData, supprimer le Content-Type pour laisser axios le gérer
          // Pour méthode GET, supprimer le Content-Type pour ne pas déclencher une requête preflight inutile
          delete config.headers["Content-Type"];
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Intercepteur pour gérer les erreurs et afficher les toasts
    this.axiosInstance.interceptors.response.use(
      (response) => {
        // Gérer les toasts de succès si configuré
        const toastOptions = (response.config as any).toastOptions as
          | ToastOptions
          | undefined;
        const options = toastOptions || this.defaultToastOptions;

        if (
          this.toastHandler &&
          options.toastOnSuccess &&
          options.successMessage
        ) {
          this.toastHandler(
            options.successMessage,
            "success",
            options.duration,
          );
        }

        return response;
      },
      (error: AxiosError) => {
        // Gérer les toasts d'erreur si configuré
        const toastOptions = (error.config as any)?.toastOptions as
          | ToastOptions
          | undefined;
        const options = toastOptions || this.defaultToastOptions;
        // Récupérer la langue depuis les options de toast ou utiliser la langue par défaut
        const language =
          ((error.config as any)?.language as WidgetLanguage | undefined) ||
          this.language;

        if (this.toastHandler && options.toastOnError) {
          const errorMessage =
            options.errorMessage ||
            this.getErrorMessage(error) ||
            TranslationService.translate("error", language);
          this.toastHandler(errorMessage, "error", options.duration);
        }

        return Promise.reject(error);
      },
    );
  }

  /**
   * Extrait un message d'erreur depuis une erreur Axios
   */
  private getErrorMessage(error: AxiosError): string | null {
    if (error.response?.data) {
      const data = error.response.data as any;
      // Essayer d'extraire un message d'erreur depuis la réponse
      if (typeof data === "string") {
        return data;
      }
      if (data.message) {
        return data.message;
      }
      if (data.error) {
        return typeof data.error === "string" ? data.error : data.error.message;
      }
    }

    if (error.message) {
      return error.message;
    }

    return null;
  }

  /**
   * Effectue une requête GET
   */
  public async get<T = any>(
    url: string,
    config?: AxiosRequestConfig & {
      toastOptions?: ToastOptions;
      language?: WidgetLanguage;
    },
  ): Promise<T> {
    const response = await this.axiosInstance.get<T>(url, {
      ...config,
      ...(config?.toastOptions &&
        ({
          toastOptions: config.toastOptions,
        } as any)),
      ...(config?.language &&
        ({
          language: config.language,
        } as any)),
    });
    return response.data;
  }

  /**
   * Effectue une requête POST
   */
  public async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig & {
      toastOptions?: ToastOptions;
      language?: WidgetLanguage;
    },
  ): Promise<T> {
    const response = await this.axiosInstance.post<T>(url, data, {
      ...config,
      ...(config?.toastOptions &&
        ({
          toastOptions: config.toastOptions,
        } as any)),
      ...(config?.language &&
        ({
          language: config.language,
        } as any)),
    });
    return response.data;
  }

  /**
   * Effectue une requête PUT
   */
  public async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig & {
      toastOptions?: ToastOptions;
      language?: WidgetLanguage;
    },
  ): Promise<T> {
    const response = await this.axiosInstance.put<T>(url, data, {
      ...config,
      ...(config?.toastOptions &&
        ({
          toastOptions: config.toastOptions,
        } as any)),
      ...(config?.language &&
        ({
          language: config.language,
        } as any)),
    });
    return response.data;
  }

  /**
   * Effectue une requête DELETE
   */
  public async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig & {
      toastOptions?: ToastOptions;
      language?: WidgetLanguage;
    },
  ): Promise<T> {
    const response = await this.axiosInstance.delete<T>(url, {
      ...config,
      ...(config?.toastOptions &&
        ({
          toastOptions: config.toastOptions,
        } as any)),
      ...(config?.language &&
        ({
          language: config.language,
        } as any)),
    });
    return response.data;
  }

  /**
   * Effectue une requête PATCH
   */
  public async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig & {
      toastOptions?: ToastOptions;
      language?: WidgetLanguage;
    },
  ): Promise<T> {
    const response = await this.axiosInstance.patch<T>(url, data, {
      ...config,
      ...(config?.toastOptions &&
        ({
          toastOptions: config.toastOptions,
        } as any)),
      ...(config?.language &&
        ({
          language: config.language,
        } as any)),
    });
    return response.data;
  }
}
